import type { JSX } from 'react';
import { Field } from '@/app/ui/Field';
import { Button, controlStyles as c, Segment, cx } from '@/app/ui/controls';
import type { ContentCheck } from '@/core/content/types';
import type { EncodeResult, ErrorCorrection } from '@/core/qr/encode';
import type { Project } from '@/core/project/types';
import type { EditorAction } from '../state';
import { IssueList } from '../IssueList';
import styles from './panels.module.css';

const ECC_OPTIONS: { value: ErrorCorrection; label: string; title: string }[] = [
  { value: 'L', label: 'L · 7%', title: 'Минимальная избыточность, самый компактный символ' },
  { value: 'M', label: 'M · 15%', title: 'Значение по умолчанию для обычного QR' },
  { value: 'Q', label: 'Q · 25%', title: 'Больше избыточности, символ крупнее' },
  { value: 'H', label: 'H · 30%', title: 'Максимальная избыточность, символ самый крупный' },
];

export type DataPanelProps = {
  project: Project;
  content: ContentCheck;
  encoded: EncodeResult;
  dispatch: (action: EditorAction) => void;
};

const ISSUES_ID = 'kurlyk-payload-issues';

export function DataPanel({ project, content, encoded, dispatch }: DataPanelProps): JSX.Element {
  const isUrl = project.payload.kind === 'url';
  const hasError = content.issues.some((i) => i.level === 'error');

  return (
    <div className={styles.panel}>
      <Field label="Что кодируем">
        {() => (
          <Segment
            name="kind"
            ariaLabel="Тип содержимого"
            value={project.payload.kind}
            options={[
              { value: 'url', label: 'Ссылка' },
              { value: 'text', label: 'Текст' },
            ]}
            onChange={(kind) => dispatch({ type: 'set-payload-kind', kind })}
          />
        )}
      </Field>

      <Field
        label="Содержимое QR"
        hint={
          isUrl
            ? 'Принимаются адреса http:// и https://. В код попадает ровно то, что здесь написано.'
            : 'Пробелы, переводы строк и Unicode сохраняются без изменений.'
        }
        messagesId={content.issues.length > 0 ? ISSUES_ID : undefined}
      >
        {({ id, describedBy }) =>
          isUrl ? (
            <input
              id={id}
              aria-describedby={describedBy}
              className={cx(c.input, c.mono, hasError && c.invalid)}
              aria-invalid={hasError}
              type="text"
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
              placeholder="https://example.org/страница"
              value={project.payload.value}
              onChange={(e) => dispatch({ type: 'set-payload-value', value: e.target.value })}
            />
          ) : (
            <textarea
              id={id}
              aria-describedby={describedBy}
              className={cx(c.textarea, c.mono, hasError && c.invalid)}
              aria-invalid={hasError}
              spellCheck={false}
              placeholder="Любой текст, включая кириллицу и переводы строк"
              value={project.payload.value}
              onChange={(e) => dispatch({ type: 'set-payload-value', value: e.target.value })}
            />
          )
        }
      </Field>

      <IssueList
        id={ISSUES_ID}
        issues={content.issues}
        onFix={(value) => dispatch({ type: 'set-payload-value', value })}
      />

      {encoded.status === 'too-long' ? (
        <p className={styles.errorBox} role="alert">
          {encoded.message}
        </p>
      ) : null}
      {encoded.status === 'error' ? (
        <p className={styles.errorBox} role="alert">
          Не удалось закодировать данные: {encoded.message}
        </p>
      ) : null}

      <Field
        label="Уровень коррекции ошибок"
        hint="Более высокий уровень увеличивает символ при тех же данных. Это восстановление кодовых слов, а не разрешённая доля площади под картинку."
      >
        {() => (
          <Segment
            name="ecc"
            ariaLabel="Уровень коррекции ошибок"
            value={project.qr.errorCorrection}
            options={ECC_OPTIONS}
            onChange={(ecc) => dispatch({ type: 'set-ecc', ecc })}
          />
        )}
      </Field>

      <Field
        label={`Защитное поле: ${project.qr.quietZoneModules} модулей`}
        hint="Минимум четыре модуля с каждой стороны — требование спецификации QR."
      >
        {({ id, describedBy }) => (
          <div className={c.row}>
            <input
              id={id}
              aria-describedby={describedBy}
              className={c.slider}
              type="range"
              min={4}
              max={12}
              step={1}
              value={project.qr.quietZoneModules}
              onChange={(e) =>
                dispatch({ type: 'set-quiet-zone', modules: Number(e.target.value) })
              }
            />
            {project.qr.quietZoneModules !== 4 ? (
              <Button variant="ghost" onClick={() => dispatch({ type: 'set-quiet-zone', modules: 4 })}>
                Вернуть 4
              </Button>
            ) : null}
          </div>
        )}
      </Field>
    </div>
  );
}
